import { AxiosError, AxiosResponse } from 'axios';
import i18n from '@/i18n';
import { generateQueryString, get, getBlob, post, put, unwrapItems } from '@/utils';
import {
	ITranslation,
	ITranslationsCsvFile,
	ITranslationsImportResult,
	ITranslationsQuery,
	TranslationsResponse
} from './types';

// ==============================|| TRANSLATIONS - ACȚIUNI (API) ||============================== //
// Fetcher-ele brute pentru api/translations*. Atenție la casing-ul query-ului: lista
// folosește parametri PascalCase (Platform, PageNumber…), bundle/groups — lowercase
// (platform, language), conform swagger-ului fiecărui endpoint.

const errorMessage = (e: unknown, fallbackKey: string): Error => {
	const data = (e as AxiosError).response?.data as { error?: string } | undefined;
	return new Error(data?.error || i18n.t(fallbackKey));
};

// GET api/translations → lista paginată (envelope cu items + meta de paginare).
async function fetchTranslations(
	{
		platform,
		language,
		group,
		search,
		onlyModified,
		page,
		items,
		orderBy,
		ascending
	}: ITranslationsQuery,
	onResponse?: (totalPages: number, totalCount: number) => void
): Promise<ITranslation[]> {
	try {
		// generateQueryString omite valorile undefined/null/'' (dar păstrează `false`).
		const query = generateQueryString({
			Platform: platform,
			Language: language,
			Group: group,
			Search: search,
			OnlyModified: onlyModified,
			PageNumber: page,
			PageSize: items,
			OrderBy: orderBy,
			Ascending: ascending
		});
		const res = await get(`api/translations${query}`);
		const data = (res.data ?? {}) as TranslationsResponse;
		onResponse?.(data.totalPages ?? 0, data.totalCount ?? 0);
		return unwrapItems<ITranslation>(data);
	} catch (e) {
		throw errorMessage(e, 'textSettings.errorLoad');
	}
}

// GET api/translations/bundle → TOATE textele platformei+limbii ca dicționar plat
// { "cheie.punctată": "valoare" }, gata de aplicat peste i18n (fără paginare/mapare).
async function fetchTranslationsBundle(
	platform: string,
	language: string
): Promise<Record<string, string>> {
	try {
		const res = await get(
			`api/translations/bundle${generateQueryString({ platform, language })}`
		);
		const data = res.data as unknown;
		return data && typeof data === 'object' && !Array.isArray(data)
			? (data as Record<string, string>)
			: {};
	} catch (e) {
		throw errorMessage(e, 'textSettings.errorLoadBundle');
	}
}

// GET api/translations/groups → grupurile distincte disponibile (string[]), pentru
// filtrul „Grup" din pagina „Gestionare texte".
async function fetchTranslationGroups(
	platform: string,
	language: string
): Promise<string[]> {
	try {
		const res = await get(
			`api/translations/groups${generateQueryString({ platform, language })}`
		);
		return Array.isArray(res.data)
			? (res.data as unknown[]).filter((g): g is string => typeof g === 'string')
			: [];
	} catch (e) {
		throw errorMessage(e, 'textSettings.errorLoadGroups');
	}
}

// GET api/translations/:id → un singur text (aceeași formă ca itemii listei).
async function fetchSingleTranslation(id: number): Promise<ITranslation> {
	try {
		const res = await get(`api/translations/${id}`);
		return res.data as ITranslation;
	} catch (e) {
		throw errorMessage(e, 'textSettings.errorLoad');
	}
}

// PUT api/translations/:id → salvează valoarea unui text (body: { value }).
async function putTranslation(id: number, value: string): Promise<void> {
	try {
		await put(`api/translations/${id}`, { value });
	} catch (e) {
		throw errorMessage(e, 'textSettings.errorSave');
	}
}

// POST api/translations/:id/reset → readuce un text la valoarea seed (default).
async function postTranslationReset(id: number): Promise<void> {
	try {
		await post(`api/translations/${id}/reset`);
	} catch (e) {
		throw errorMessage(e, 'textSettings.errorReset');
	}
}

// POST api/translations/reset → readuce la seed textele modificate ale platformei+limbii
// din query (lowercase, ca la bundle/groups) — resetul rămâne limitat la scopul curent.
async function postTranslationsResetAll(
	platform: string,
	language: string
): Promise<void> {
	try {
		await post(`api/translations/reset${generateQueryString({ platform, language })}`);
	} catch (e) {
		throw errorMessage(e, 'textSettings.errorReset');
	}
}

// Blob + numele fișierului din Content-Disposition (dacă BE îl trimite).
const toCsvFile = (res: AxiosResponse<Blob>): ITranslationsCsvFile => {
	const disposition = String(res.headers?.['content-disposition'] ?? '');
	const match = /filename\*?=(?:UTF-8'')?"?([^";]+)/i.exec(disposition);
	return { blob: res.data, fileName: match ? decodeURIComponent(match[1]) : null };
};

// GET api/translations/export → CSV cu textele. Cu Platform+Language exportă doar
// scopul respectiv; FĂRĂ parametri exportă tot — opțiunea „Toate scopurile".
async function fetchTranslationsExport(
	platform?: string,
	language?: string
): Promise<ITranslationsCsvFile> {
	try {
		return toCsvFile(
			await getBlob(
				`api/translations/export${generateQueryString({ Platform: platform, Language: language })}`
			)
		);
	} catch {
		// La responseType blob, corpul erorii e tot un Blob — nu putem extrage mesajul BE.
		throw new Error(i18n.t('textSettings.errorExport'));
	}
}

// GET api/translations/import-template → CSV-model pentru import: coloanele așteptate,
// pre-completat cu cheile reale ale scopului cerut (se editează și se importă ca atare).
async function fetchTranslationsImportTemplate(
	platform: string,
	language: string
): Promise<ITranslationsCsvFile> {
	try {
		return toCsvFile(
			await getBlob(
				`api/translations/import-template${generateQueryString({ platform, language })}`
			)
		);
	} catch {
		// La responseType blob, corpul erorii e tot un Blob — nu putem extrage mesajul BE.
		throw new Error(i18n.t('textSettings.errorTemplate'));
	}
}

// POST api/translations/import → importă un CSV (multipart, câmpul „file"). Aplicare
// all-or-nothing: la orice rând invalid nimic nu se salvează, iar problemele vin cu
// linia/coloana lor. Un răspuns de validare venit pe 4xx e întors tot ca rezultat
// (success=false), nu ca eroare — UI-ul afișează lista de probleme în ambele cazuri.
async function postTranslationsImport(file: File): Promise<ITranslationsImportResult> {
	const body = new FormData();
	body.append('file', file);
	try {
		const res = await post('api/translations/import', body);
		return res.data as ITranslationsImportResult;
	} catch (e) {
		const data = (e as AxiosError).response?.data as
			| ITranslationsImportResult
			| undefined;
		if (data && Array.isArray(data.errors)) return { ...data, success: false };
		throw errorMessage(e, 'textSettings.errorImport');
	}
}

export {
	fetchTranslations,
	fetchTranslationsBundle,
	fetchTranslationGroups,
	fetchSingleTranslation,
	putTranslation,
	postTranslationReset,
	postTranslationsResetAll,
	fetchTranslationsExport,
	fetchTranslationsImportTemplate,
	postTranslationsImport
};
